/**
 * What a free-form tag editor may show, and how its result folds back into the record.
 *
 * A `tags` map is one bag holding three different kinds of thing: values with a dedicated control of
 * their own, values only the raw editor can reach, and internal markers nobody should be editing by
 * hand. Handing the bag to the editor as-is would put two controls on one value and invite a null to
 * be saved back as a string, so the editor gets a filtered view and its output is merged, never
 * assigned.
 *
 * Shared by the sector and profile forms so the two cannot drift apart.
 */

export type DataTags = Record<string, unknown>

/**
 * The subset of `tags` the editor may render: string values without a field of their own.
 *
 * Non-strings are withheld deliberately. Six of Forsthofgut's nine sectors carry `crqlarV2sku: null`,
 * a migration marker, and the editor's schema declares string values; rendering that null would show
 * a blank row that saves back as `""` and quietly rewrites the marker.
 */
export const editableDataTags = (
    tags?: DataTags | null,
    reserved: readonly string[] = [],
): Record<string, string> => (
    Object.entries(tags ?? {}).reduce<Record<string, string>>((acc, [key, value]) => {
        if (!reserved.includes(key) && typeof value === 'string') acc[key] = value
        return acc
    }, {})
)

/**
 * The record's new tags, given what the editor now holds.
 *
 * Built from the editor's payload plus everything the editor was never shown, so a round trip through
 * it cannot delete what it could not display. A key the editor dropped is genuinely deleted; a key it
 * never had is untouched. A reserved key typed in by hand is ignored, because the labelled field owns
 * that value and two writers for one tag is how they end up disagreeing.
 */
export const mergeDataTags = (
    existing?: DataTags | null,
    edited?: DataTags | null,
    reserved: readonly string[] = [],
): DataTags => {
    const withheld = Object.entries(existing ?? {})
        .filter(([key, value]) => reserved.includes(key) || typeof value !== 'string')
    const kept = Object.entries(edited ?? {})
        .filter(([key]) => !reserved.includes(key))

    return Object.fromEntries([...withheld, ...kept])
}
